import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../utils/api.js";
import Toastify from "../utils/Toastify.js";

const READ_NOTIFS_KEY = "electrowiz_read_notification_ids";
const ALL_READ_KEY = "electrowiz_all_notifications_read";

const isAllNotificationsRead = () => {
    try {
        return localStorage.getItem(ALL_READ_KEY) === "true";
    } catch (e) {
        return false;
    }
};

const getReadNotificationIds = () => {
    try {
        const stored = localStorage.getItem(READ_NOTIFS_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        return [];
    }
};

const saveReadNotificationId = (id) => {
    try {
        const readIds = getReadNotificationIds();
        if (!readIds.includes(id)) {
            readIds.push(id);
            localStorage.setItem(READ_NOTIFS_KEY, JSON.stringify(readIds));
        }
    } catch (e) {
        console.error("Failed to save read notification ID to localStorage", e);
    }
};

const saveAllReadNotificationIds = (ids) => {
    try {
        localStorage.setItem(ALL_READ_KEY, "true");
        const readIds = getReadNotificationIds();
        const updated = Array.from(new Set([...readIds, ...ids, "mock-1", "mock-2", "mock-3", "mock-4", "mock-5"]));
        localStorage.setItem(READ_NOTIFS_KEY, JSON.stringify(updated));
    } catch (e) {
        console.error("Failed to save read notification IDs to localStorage", e);
    }
};

const applyReadStateToMockNotifications = (notifications) => {
    const allRead = isAllNotificationsRead();
    const readIds = getReadNotificationIds();
    return notifications.map(n => {
        if (n && n._id && (allRead || readIds.includes(n._id))) {
            return { ...n, isRead: true };
        }
        return n;
    });
};

const sampleNotifications = [
    {
        _id: "mock-1",
        title: "BOM Quote Requested",
        message: "Your quote request for BOM #BOM-8902 has been sent to suppliers. You will be notified as soon as a quote is submitted.",
        notificationType: "quota",
        isRead: false,
        createrId: "admin",
        createdAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    },
    {
        _id: "mock-2",
        title: "Quote Reply Received",
        message: "Admin has replied with a quotation for your BOM #BOM-8902. Open details to review pricing and lead times.",
        notificationType: "reply",
        isRead: false,
        createrId: "admin",
        createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    },
    {
        _id: "mock-3",
        title: "Order Placed Successfully",
        message: "Thank you! Your purchase order #PO-20931 (4 items) has been received and is currently being processed.",
        notificationType: "order",
        isRead: false,
        createrId: "admin",
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    },
    {
        _id: "mock-4",
        title: "Security Alert: Login Detected",
        message: "New login detected on Chrome for Windows (IP: 106.220.14.92) at 03:54 PM. If this wasn't you, please secure your account.",
        notificationType: "login",
        isRead: false,
        createrId: "admin",
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    },
    {
        _id: "mock-5",
        title: "Cart Items Saved",
        message: "You have left 3 items in your cart. Check out now to secure current pricing and shipping slots.",
        notificationType: "cart",
        isRead: false,
        createrId: "admin",
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
    }
];

const initialMockNotifications = applyReadStateToMockNotifications(sampleNotifications);

const initialState = {
    isRefresh: false,
    isLoading: false,
    notificationData: initialMockNotifications,
    notificationCountData: initialMockNotifications.filter(n => n && !n.isRead).length,
    createdNotificationData:{},
    errorMsg: "",
    isError: false,
};

export const getNotification = createAsyncThunk('getNotification', async ({body,showAll}, { rejectWithValue, dispatch }) => {
    try {              
        const { data, status } = await api.getNotification(body,showAll);
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
}
);

export const getNotificationCount = createAsyncThunk('getNotificationCount', async (body, { rejectWithValue, dispatch }) => {
    try {
        const { data, status } = await api.getNotificationCount(body);
        if (status === 200) {
            dispatch(setNotificationCount(data.data))
        }
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
}
);

export const markAsRead=createAsyncThunk('markAsRead',async(body, { rejectWithValue, dispatch })=>{
    try {
        const { data, status } = await api.markAsRead(body);
        if (status === 200) {
            dispatch(updateNotificationStatus(body))
        }
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
});

export const markAllAsRead=createAsyncThunk('markAllAsRead',async(body,{rejectWithValue,dispatch})=>{
    try {
        const { data, status } = await api.markAllAsReaded(body);
        if(status === 200){
            dispatch(updateAllNotificationMarkAsRead())
            Toastify.success("Marked all notifications as read ✅ ");
        }
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
});

export const notificationSlice = createSlice({
    name: "notification",
    initialState,
    reducers: {
        setNotification: (state, action) => {
            if (Array.isArray(action.payload)) {
                state.notificationData = action.payload;
            } else if (action.payload && typeof action.payload === 'object') {
                state.notificationData = [action.payload, ...state.notificationData];
            }
            state.notificationCountData = state.notificationData.filter(n => n && !n.isRead).length;
        },
        setCreatednotification:(state,action)=>{
             state.createdNotificationData=action.payload
        },
        setNotificationCount: (state, action) => {
            const hasRealNotifications = state.notificationData?.some(n => n && n._id && !n._id.startsWith('mock-'));
            if (!hasRealNotifications && state.notificationData?.length > 0) {
                state.notificationCountData = state.notificationData.filter(n => n && !n.isRead).length;
            } else {
                state.notificationCountData = typeof action.payload === 'number' ? action.payload : 0;
            }
        },
        setRefresh:(state) => {
            state.isRefresh = !state.isRefresh
        },
        addNotification: (state, action) => {
            if (action.payload && typeof action.payload === 'object') {
                state.notificationData = [action.payload, ...state.notificationData];
                if (!action.payload.isRead) {
                    state.notificationCountData += 1;
                }
            }
        },
        updateNotificationStatus:(state,action)=>{
            const idToMark = action.payload;
            saveReadNotificationId(idToMark);

            let wasUnread = false;
            state.notificationData = state.notificationData?.map((notification) => {
                if (notification && notification._id === idToMark) {
                    if (!notification.isRead) {
                        wasUnread = true;
                    }
                    return {
                        ...notification,
                        isRead: true
                    };
                }
                return notification;
            });

            if (wasUnread) {
                state.notificationCountData = Math.max(0, state.notificationCountData - 1);
            }
        },
        updateAllNotificationMarkAsRead:(state,action)=>{
            const allIds = state.notificationData?.map(n => n && n._id).filter(Boolean) || [];
            saveAllReadNotificationIds(allIds);

            state.notificationData = state.notificationData?.map((notification) => {
                if (notification && typeof notification === 'object') {
                    return {
                        ...notification,
                        isRead: true
                    };
                }
                return notification;
            });
            state.notificationCountData = 0;
        }
    },

    extraReducers: (builder) => {
        builder.addCase(getNotification.pending, (state) => {
            state.isLoading = true;
            state.isError = false;
        });
        builder.addCase(getNotification.fulfilled, (state, action) => {
            state.isLoading = false;
            if (action.payload && action.payload.length > 0) {
                state.notificationData = action.payload;
                state.notificationCountData = action.payload.filter(n => n && !n.isRead).length;
            } else {
                const readStateData = applyReadStateToMockNotifications(sampleNotifications);
                state.notificationData = readStateData;
                state.notificationCountData = readStateData.filter(n => n && !n.isRead).length;
            }
        });
        builder.addCase(getNotification.rejected, (state, action) => {
            state.isLoading = false;
            state.errorMsg = action.payload;
        });
    }
});

export const { setNotification, setNotificationCount, setRefresh, addNotification, updateNotificationStatus, updateAllNotificationMarkAsRead, setCreatednotification } = notificationSlice.actions;

export default notificationSlice.reducer;
